import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { FormEvent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  BinGenerationRequest,
  BinRange,
  WarehouseNode,
  generateBins,
  previewBinGeneration
} from "./api";
import {
  measurementInputStep, measurementInputValue, measurementUnit, toMetric
} from "./measurements";
import { useMeasurementSettings } from "./useMeasurementSettings";

const organizationId = "10000000-0000-0000-0000-000000000001";

interface Props {
  open: boolean;
  nodes: WarehouseNode[];
  onClose: () => void;
  onGenerated: () => Promise<void>;
}

interface RangeEditorProps {
  label: string;
  value: BinRange;
  onChange: (value: BinRange) => void;
}

function RangeEditor({ label, value, onChange }: RangeEditorProps) {
  const { t } = useTranslation();
  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>{label}</Typography>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
        <TextField label={t("warehouse.generator.ranges.prefix")} value={value.prefix} sx={{ maxWidth: { sm: 100 } }}
          onChange={(event) => onChange({ ...value, prefix: event.target.value.toUpperCase() })} />
        <TextField label={t("warehouse.generator.ranges.from")} type="number" value={value.from}
          onChange={(event) => onChange({ ...value, from: Number(event.target.value) })} />
        <TextField label={t("warehouse.generator.ranges.to")} type="number" value={value.to}
          onChange={(event) => onChange({ ...value, to: Number(event.target.value) })} />
        <TextField label={t("warehouse.generator.ranges.digits")} type="number" value={value.padding}
          inputProps={{ min: 1, max: 4 }}
          onChange={(event) => onChange({ ...value, padding: Number(event.target.value) })} />
      </Stack>
    </Box>
  );
}

export function BinGeneratorDialog({ open, nodes, onClose, onGenerated }: Props) {
  const { t } = useTranslation();
  const { configuration: measurements } = useMeasurementSettings();
  const lengthUnit = measurementUnit("LENGTH", measurements);
  const massUnit = measurementUnit("MASS", measurements);
  const lengthStep = measurementInputStep("LENGTH", measurements);
  const massStep = measurementInputStep("MASS", measurements);
  const parents = nodes.filter((node) =>
    node.type === "STORAGE_AREA" || node.type === "STORAGE_SECTION"
  );
  const [parentId, setParentId] = useState("");
  const [pattern, setPattern] = useState("{aisle}-{bay}-{level}-{position}");
  const [aisle, setAisle] = useState<BinRange>({ prefix: "A", from: 1, to: 1, padding: 2 });
  const [bay, setBay] = useState<BinRange>({ prefix: "B", from: 1, to: 2, padding: 2 });
  const [level, setLevel] = useState<BinRange>({ prefix: "L", from: 1, to: 2, padding: 2 });
  const [position, setPosition] = useState<BinRange>({ prefix: "P", from: 1, to: 1, padding: 2 });
  const [length, setLength] = useState("1200");
  const [width, setWidth] = useState("800");
  const [height, setHeight] = useState("1400");
  const [openingWidth, setOpeningWidth] = useState("780");
  const [openingHeight, setOpeningHeight] = useState("1350");
  const [maxWeight, setMaxWeight] = useState("1000");
  const [maxHandlingUnits, setMaxHandlingUnits] = useState("1");

  useEffect(() => {
    setLength(measurementInputValue(1200, "LENGTH", measurements));
    setWidth(measurementInputValue(800, "LENGTH", measurements));
    setHeight(measurementInputValue(1400, "LENGTH", measurements));
    setOpeningWidth(measurementInputValue(780, "LENGTH", measurements));
    setOpeningHeight(measurementInputValue(1350, "LENGTH", measurements));
    setMaxWeight(measurementInputValue(1000, "MASS", measurements));
  }, [measurements]);

  const request = useMemo<BinGenerationRequest>(() => ({
    organizationId,
    parentId,
    codePattern: pattern,
    aisle,
    bay,
    level,
    position,
    binType: "STORAGE",
    usableLengthMm: Number(length) ? toMetric(Number(length), "LENGTH", measurements) : undefined,
    usableWidthMm: Number(width) ? toMetric(Number(width), "LENGTH", measurements) : undefined,
    usableHeightMm: Number(height) ? toMetric(Number(height), "LENGTH", measurements) : undefined,
    openingWidthMm: Number(openingWidth) ? toMetric(Number(openingWidth), "LENGTH", measurements) : undefined,
    openingHeightMm: Number(openingHeight) ? toMetric(Number(openingHeight), "LENGTH", measurements) : undefined,
    maxWeightKg: Number(maxWeight) ? toMetric(Number(maxWeight), "MASS", measurements) : undefined,
    maxHandlingUnits: Number(maxHandlingUnits) || undefined,
    travelSequenceStart: 100
  }), [
    parentId, pattern, aisle, bay, level, position, length, width, height,
    openingWidth, openingHeight, maxWeight, maxHandlingUnits, measurements
  ]);

  const preview = useMutation({ mutationFn: previewBinGeneration });
  const generate = useMutation({
    mutationFn: generateBins,
    onSuccess: async () => {
      await onGenerated();
    }
  });

  function submitPreview(event: FormEvent) {
    event.preventDefault();
    preview.mutate(request);
  }

  function close() {
    preview.reset();
    generate.reset();
    onClose();
  }

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="md">
      <Box component="form" onSubmit={submitPreview}>
        <DialogTitle>{t("warehouse.generator.title")}</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            <FormControl required fullWidth>
              <InputLabel>{t("warehouse.generator.parent")}</InputLabel>
              <Select value={parentId} label={t("warehouse.generator.parent")}
                onChange={(event) => {
                  setParentId(event.target.value);
                  preview.reset();
                }}>
                {parents.map((node) => (
                  <MenuItem key={node.id} value={node.id}>
                    {node.code} · {node.displayName} ({t(node.type === "STORAGE_AREA" ? "warehouse.generator.zone" : "warehouse.generator.section")})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField label={t("warehouse.generator.pattern")} value={pattern} required
              helperText={t("warehouse.generator.variables")}
              onChange={(event) => {
                setPattern(event.target.value);
                preview.reset();
              }} />

            <RangeEditor label={t("warehouse.generator.ranges.aisles")} value={aisle} onChange={(value) => {
              setAisle(value); preview.reset();
            }} />
            <RangeEditor label={t("warehouse.generator.ranges.bays")} value={bay} onChange={(value) => {
              setBay(value); preview.reset();
            }} />
            <RangeEditor label={t("warehouse.generator.ranges.levels")} value={level} onChange={(value) => {
              setLevel(value); preview.reset();
            }} />
            <RangeEditor label={t("warehouse.generator.ranges.positions")} value={position} onChange={(value) => {
              setPosition(value); preview.reset();
            }} />

            <Divider />
            <Typography variant="h6">{t("warehouse.generator.physicalTemplate")}</Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <TextField label={`${t("warehouse.generator.usableLength")} (${lengthUnit})`} type="number" value={length}
                inputProps={{ min: lengthStep, step: lengthStep }}
                onChange={(event) => setLength(event.target.value)} />
              <TextField label={`${t("warehouse.generator.usableWidth")} (${lengthUnit})`} type="number" value={width}
                inputProps={{ min: lengthStep, step: lengthStep }}
                onChange={(event) => setWidth(event.target.value)} />
              <TextField label={`${t("warehouse.generator.usableHeight")} (${lengthUnit})`} type="number" value={height}
                inputProps={{ min: lengthStep, step: lengthStep }}
                onChange={(event) => setHeight(event.target.value)} />
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <TextField label={`${t("warehouse.generator.openingWidth")} (${lengthUnit})`} type="number" value={openingWidth}
                inputProps={{ min: lengthStep, step: lengthStep }}
                onChange={(event) => setOpeningWidth(event.target.value)} />
              <TextField label={`${t("warehouse.generator.openingHeight")} (${lengthUnit})`} type="number" value={openingHeight}
                inputProps={{ min: lengthStep, step: lengthStep }}
                onChange={(event) => setOpeningHeight(event.target.value)} />
              <TextField label={`${t("warehouse.generator.maxWeight")} (${massUnit})`} type="number" value={maxWeight}
                inputProps={{ min: massStep, step: massStep }}
                onChange={(event) => setMaxWeight(event.target.value)} />
              <TextField label={t("warehouse.generator.maxHandlingUnits")} type="number" value={maxHandlingUnits}
                onChange={(event) => setMaxHandlingUnits(event.target.value)} />
            </Stack>

            {(preview.isError || generate.isError) && (
              <Alert severity="error">
                {t("warehouse.generator.error")}
              </Alert>
            )}

            {preview.data && (
              <>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip label={t("warehouse.generator.calculated", { count: preview.data.totalCount })} />
                  <Chip color="success" label={t("warehouse.generator.new", { count: preview.data.newCount })} />
                  <Chip color={preview.data.duplicateCount ? "error" : "default"}
                    label={t("warehouse.generator.duplicates", { count: preview.data.duplicateCount })} />
                </Stack>
                <TableContainer sx={{ maxHeight: 300 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t("warehouse.generator.code")}</TableCell>
                        <TableCell>{t("warehouse.bin.metrics.aisle")}</TableCell>
                        <TableCell>{t("warehouse.bin.metrics.bay")}</TableCell>
                        <TableCell>{t("warehouse.bin.metrics.level")}</TableCell>
                        <TableCell>{t("warehouse.bin.metrics.position")}</TableCell>
                        <TableCell>{t("warehouse.generator.state")}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {preview.data.candidates.slice(0, 100).map((candidate) => (
                        <TableRow key={candidate.code}>
                          <TableCell>{candidate.code}</TableCell>
                          <TableCell>{candidate.aisle}</TableCell>
                          <TableCell>{candidate.bay}</TableCell>
                          <TableCell>{candidate.level}</TableCell>
                          <TableCell>{candidate.position}</TableCell>
                          <TableCell>
                            <Chip size="small" color={candidate.status === "NEW" ? "success" : "error"}
                              label={t(candidate.status === "NEW" ? "warehouse.generator.candidateNew" : "warehouse.generator.exists")} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {preview.data.totalCount > 100 && (
                  <Typography variant="caption" color="text.secondary">
                    {t("warehouse.generator.firstHundred")}
                  </Typography>
                )}
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={close}>{t("common.actions.close")}</Button>
          <Button type="submit" variant="outlined" disabled={!parentId || preview.isPending}>
            {t("warehouse.generator.preview")}
          </Button>
          <Button variant="contained" disabled={
            !preview.data || preview.data.duplicateCount > 0 || generate.isPending
          } onClick={() => generate.mutate(request)}>
            {t("warehouse.generator.create", { count: preview.data?.newCount ?? 0 })}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
